import {
  BlueprintTaskTemplate,
  DuplicateProjectPreview,
  TemplateSnapshot,
} from '@staiter/shared/task-module';
import { z } from 'zod';
import { apiRequest } from '~/lib/api/client';

/**
 * Blueprint (шаблоны проектов) API-клиент — project-templates F1.1.
 *
 * Единый typed-контракт для страницы шаблонов и трёх диалогов. Схемы снапшота —
 * из `@staiter/shared` (SSOT), не локальные interface. Все ответы проходят
 * `Schema.parse` до Query cache (невалидный → ApiRequestError, не тихая деградация).
 */

// ─── Skip / summary ──────────────────────────────────────────────────

export const TemplateSkippedSchema = z.record(z.string(), z.number()).default({});
export type TemplateSkipped = z.infer<typeof TemplateSkippedSchema>;

export const DroppedAssignee = z.object({ user_id: z.string() });
export type DroppedAssignee = z.infer<typeof DroppedAssignee>;

const OpResult = z.object({
  project_id: z.string(),
  task_count: z.number(),
  dependency_count: z.number(),
  skipped: TemplateSkippedSchema.optional(),
  dropped_assignees: z.array(DroppedAssignee).optional().default([]),
});
export type OpResult = z.infer<typeof OpResult>;

// ─── List ────────────────────────────────────────────────────────────

const BlueprintListItem = z.object({
  id: z.string(),
  entity_id: z.string(),
  name: z.string(),
  kind: z.string(),
  version: z.number(),
  /** B2 — линия версий (rename не рвёт историю). */
  lineage_id: z.string(),
  is_active: z.boolean(),
  description: z.string().nullable(),
  updated_at: z.string(),
  task_count: z.number(),
  is_v2: z.boolean(),
  source: z.object({ project_name: z.string(), project_code: z.string().nullable() }).nullable(),
  previous_versions_count: z.number(),
  /**
   * B14/F6 — «план: N дней» на карточке. `null` = смещений в шаблоне нет вовсе
   * («нет дат»), а не «план на 0 дней»: разница существенная, поэтому не 0.
   */
  max_offset_days: z.number().nullable(),
  has_dates: z.boolean(),
});
export type BlueprintListItem = z.infer<typeof BlueprintListItem>;

const BlueprintsListResponse = z
  .object({ blueprints: z.array(BlueprintListItem) })
  .transform((d) => d.blueprints);

export function fetchBlueprints() {
  return apiRequest('/task-module/blueprints', BlueprintsListResponse);
}

// ─── Detail ──────────────────────────────────────────────────────────

const BlueprintDetail = z.object({
  id: z.string(),
  entity_id: z.string(),
  name: z.string(),
  kind: z.string(),
  version: z.number(),
  lineage_id: z.string(),
  is_active: z.boolean(),
  description: z.string().nullable(),
  snapshot: TemplateSnapshot.nullable(),
  /** v1-шаблон (`snapshot === null`) несёт плоский список задач — F4 рендерит его. */
  tasks: z.array(BlueprintTaskTemplate).default([]),
  created_by_user_id: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type BlueprintDetail = z.infer<typeof BlueprintDetail>;

/** B15 — денорм имён рядом с ответом (паттерн task-ручек), без второго запроса. */
const UserDirectoryEntry = z.object({
  user_id: z.string(),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
});
export type UserDirectoryEntry = z.infer<typeof UserDirectoryEntry>;
const UserDirectory = z.array(UserDirectoryEntry).default([]);

/**
 * F-C14 — имя автора из денорм-директории.
 *
 * Веток две, и обе живые: запись есть → показываем `display_name` (бэкенд уже
 * складывает его цепочкой ФИО → логин → префикс id); записи нет → возвращаем
 * `null`, и вызывающий говорит «сохранено {когда}» без автора. Подставлять
 * вместо имени сырой uuid нельзя — это не опознание человека, а шум.
 */
export function resolveUserName(
  directory: readonly UserDirectoryEntry[],
  userId: string,
): string | null {
  const hit = directory.find((u) => u.user_id === userId);
  const name = hit?.display_name?.trim();
  return name ? name : null;
}

const BlueprintDetailResponse = z
  .object({ blueprint: BlueprintDetail, assignee_directory: UserDirectory })
  .transform((d) => ({ ...d.blueprint, assignee_directory: d.assignee_directory }));

export function fetchBlueprint(id: string) {
  return apiRequest(`/task-module/blueprints/${id}`, BlueprintDetailResponse);
}

/** Ответы мутаций отдают только blueprint — директория там не нужна. */
const BlueprintOnlyResponse = z
  .object({ blueprint: BlueprintDetail })
  .transform((d) => d.blueprint);

// ─── Создание с нуля (B7 / F14) ──────────────────────────────────────

export type CreateBlueprintInput = {
  name: string;
  kind?: string;
  description?: string | null;
};

export function createBlueprint(input: CreateBlueprintInput) {
  return apiRequest('/task-module/blueprints', BlueprintOnlyResponse, {
    method: 'POST',
    body: input,
  });
}

// ─── Правка = новая версия (B6 / F12) ────────────────────────────────

export type UpdateBlueprintInput = {
  name?: string;
  kind?: string;
  description?: string | null;
  snapshot: TemplateSnapshot;
  /**
   * Версия, от которой правили. Сервер сравнивает её с активной и на расхождении
   * отвечает 409 — иначе вкладка, открытая полчаса назад, молча топит чужую правку.
   * Повтор «сохранить поверх» шлётся уже без этого поля.
   */
  base_version?: number;
};

export function updateBlueprint(id: string, input: UpdateBlueprintInput) {
  return apiRequest(`/task-module/blueprints/${id}`, BlueprintOnlyResponse, {
    method: 'PUT',
    body: input,
  });
}

// ─── Версии линии (B8 / F2) ──────────────────────────────────────────

const BlueprintVersion = z.object({
  id: z.string(),
  version: z.number(),
  is_active: z.boolean(),
  /** Имя версии: после переименования версии линии носят разные имена. */
  name: z.string(),
  is_v2: z.boolean(),
  task_count: z.number(),
  created_by_user_id: z.string(),
  created_at: z.string(),
});
export type BlueprintVersion = z.infer<typeof BlueprintVersion>;

const BlueprintVersionsResponse = z
  .object({ versions: z.array(BlueprintVersion), assignee_directory: UserDirectory })
  .transform((d) => ({ versions: d.versions, directory: d.assignee_directory }));

export function fetchBlueprintVersions(id: string) {
  return apiRequest(`/task-module/blueprints/${id}/versions`, BlueprintVersionsResponse);
}

/**
 * B8/F3 — restore: НЕ переключение флага, а вставка копии выбранной версии как
 * новой активной (append-only, §4.4). Ответ — созданная строка vN+1.
 */
export function activateBlueprintVersion(id: string) {
  return apiRequest(`/task-module/blueprints/${id}/activate`, BlueprintOnlyResponse, {
    method: 'POST',
  });
}

/** B9/F4 — апгрейд v1-строки в v2 новой версией той же линии. */
export function convertBlueprintToV2(id: string) {
  return apiRequest(`/task-module/blueprints/${id}/convert`, BlueprintOnlyResponse, {
    method: 'POST',
  });
}

// ─── Materialize ─────────────────────────────────────────────────────

export type MaterializeInput = {
  start_date: string;
  date_mode?: 'start_on' | 'finish_by';
  name?: string;
  project_type?: string | null;
  estimated_cost?: string | null;
  notes?: string | null;
  customer_id?: string | null;
  sales_order_id?: string | null;
  copy_assignees?: boolean;
  /** project-visibility (F12) — видимость материализованного проекта (дефолт open). */
  visibility?: 'open' | 'private';
};

export function materializeBlueprint(id: string, input: MaterializeInput) {
  return apiRequest(`/task-module/blueprints/${id}/materialize`, OpResult, {
    method: 'POST',
    body: { source_type: null, source_id: null, ...input },
  });
}

// ─── Save as template ────────────────────────────────────────────────

const SaveResult = z.object({
  blueprint_id: z.string().nullable(),
  version: z.number(),
  counts: z.record(z.string(), z.number()),
  skipped: TemplateSkippedSchema,
  warnings: z.array(z.string()),
  anchor_task_key: z.string().nullable(),
  anchor_is_due_only: z.boolean(),
});
export type SaveResult = z.infer<typeof SaveResult>;

export type SaveAsTemplateInput = {
  name: string;
  description?: string;
  include_assignees?: boolean;
  exclude_done?: boolean;
  blueprint_id?: string;
};

export function saveProjectAsTemplate(projectId: string, input: SaveAsTemplateInput) {
  return apiRequest(`/task-module/projects/${projectId}/save-as-template`, SaveResult, {
    method: 'POST',
    body: input,
  });
}

// ─── Duplicate ───────────────────────────────────────────────────────

export type DuplicateInput = {
  name: string;
  /**
   * F16/B11 — обязательное. Бэкенд убрал default: «менять default для симметрии»
   * значило бы тихо сменить семантику для будущих вызывающих, поэтому режим дат
   * копия всегда называет явно.
   */
  dates: 'shift' | 'as_is';
  anchor_date?: string;
  statuses?: 'reset' | 'keep' | 'incomplete_only';
  copy_assignees?: boolean;
  copy_customer?: boolean;
  copy_members?: boolean;
  reattach_secondary?: boolean;
  /** project-visibility (F12) — видимость копии; без значения бэкенд наследует источник. */
  visibility?: 'open' | 'private';
};

/** B25/F64 (TPL-F9) — последствия опции «Перепривязать связанные задачи». */
export function fetchDuplicatePreview(projectId: string) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(projectId)}/duplicate/preview`,
    DuplicateProjectPreview,
  );
}

export function duplicateProject(projectId: string, input: DuplicateInput) {
  return apiRequest(`/task-module/projects/${projectId}/duplicate`, OpResult, {
    method: 'POST',
    body: input,
  });
}

// ─── Lifecycle ───────────────────────────────────────────────────────

const DeleteResult = z.object({ ok: z.boolean(), hard: z.boolean() });

export function deactivateBlueprint(id: string) {
  return apiRequest(`/task-module/blueprints/${id}`, DeleteResult, { method: 'DELETE' });
}

export function deleteBlueprint(id: string) {
  return apiRequest(`/task-module/blueprints/${id}?hard=true`, DeleteResult, { method: 'DELETE' });
}

// ─── Anchor preview (pure) ───────────────────────────────────────────

export type AnchorPreview = {
  anchor_task_key: string | null;
  anchor_is_due_only: boolean;
  max_offset_days: number;
  has_dates: boolean;
};

/**
 * Считает превью якоря из снапшота (для диалога материализации F2.3).
 * anchor_is_due_only — есть даты, но ни одной start-стороны (стиль «одни дедлайны»).
 */
export function deriveAnchorPreview(snapshot: TemplateSnapshot | null): AnchorPreview {
  if (!snapshot) {
    return {
      anchor_task_key: null,
      anchor_is_due_only: false,
      max_offset_days: 0,
      has_dates: false,
    };
  }
  let maxOffset = 0;
  let hasStart = false;
  let hasAnyDate = false;
  let anchorTaskKey: string | null = null;
  // F6 — якорь берётся по ГЛОБАЛЬНОМУ минимуму: одна планка на обе стороны.
  // Раньше due-ветка обновляла якорь только при `minStart === null`, поэтому
  // задача со сроком раньше самого раннего старта якорем не становилась —
  // превью называло «Днём 0» не ту задачу, ровно ту недоверчивость и подтверждая,
  // которую эта страница лечит.
  let minOffset: number | null = null;

  const considerAnchor = (key: string, offset: number): void => {
    if (minOffset === null || offset < minOffset) {
      minOffset = offset;
      anchorTaskKey = key;
    }
  };

  for (const t of snapshot.tasks) {
    if (t.start_offset_days !== null) {
      hasStart = true;
      hasAnyDate = true;
      maxOffset = Math.max(maxOffset, t.start_offset_days);
      considerAnchor(t.key, t.start_offset_days);
    }
    if (t.due_offset_days !== null) {
      hasAnyDate = true;
      maxOffset = Math.max(maxOffset, t.due_offset_days);
      considerAnchor(t.key, t.due_offset_days);
    }
  }
  for (const ms of snapshot.milestones) {
    if (ms.deadline_offset_days !== null) {
      hasAnyDate = true;
      maxOffset = Math.max(maxOffset, ms.deadline_offset_days);
    }
  }
  return {
    anchor_task_key: anchorTaskKey,
    anchor_is_due_only: hasAnyDate && !hasStart,
    max_offset_days: maxOffset,
    has_dates: hasAnyDate,
  };
}

/** Сдвиг ISO-даты на N дней (для расчётного превью первой/последней даты). */
export function shiftIsoDate(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}
